/**
 * The three views of the « Tableau de bord » (direction C): Semaine, Mois,
 * Année. Each view composes the shared C components; styles live in
 * dashboard-c.css. Every clickable item opens THAT item (session, invoice,
 * deal, client, month) or is a collective command whose label names its
 * destination; absent figures become honest states with the next click, or
 * stay non-interactive with an ⓘ explanation when no screen can open the
 * items concerned (never a fallback to a generic list).
 */
import { Fragment, useId, useMemo, useState, type ReactNode } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Chip, ClientLogo, ClientName, ClientTitleLogo, HonestState, InfoHint, Panel, ProgressBar, ProviderLogo, StatCard, withProviderLogo, type Tone } from '../../components/c';
import { fetchTabData, type MyWeekItem, type MyWeekPayload } from '../../lib/api';
import { formatCurrency, formatDays, safeString, toNumber } from '../../lib/format';
import type { DashboardPayload, TabEntity } from '../../lib/types';
import { FixActionButton, SyncSourceButton } from '../shared/FixAction';
import { SOURCE_PROJECTION_QUERY_OPTIONS } from '../shared/integrationProjection';
import { myWeekItemClient, type ItemClient } from '../shared/itemClient';
import {
  toCalendrierTarget,
  toClientsTarget,
  toMissionsTarget,
  toPipelineTarget,
} from '../shared/navigation';
import {
  CAUSE_STATUS_LABEL,
  CONNECTOR_LABEL,
  INTERNAL_DAY_COST_TARGET,
  diagnoseSource,
  incompleteInvoicesTarget,
  sourceStateTitle,
  syncAction,
  type SourceConnector,
  type SourceFix,
} from '../shared/sourceFix';
import {
  agendaEntryTarget,
  buildCategories,
  buildLoadMonths,
  buildMoneyFlow,
  buildTodoActions,
  buildTopClients,
  calendarRangeForMonth,
  financialMetricsOf,
  formatDateRangeShort,
  type InvoiceToSend,
  type PlannedInvoiceState,
  fromMyWeekItem,
  groupOngoingEntries,
  layoutWeek,
  monthGrid,
  monthName,
  monthTitle,
  optionalNumber,
  overlapsRange,
  parseAgendaEntries,
  parseIsoDate,
  realisedMissing,
  sessionTypeSummary,
  shortDate,
  splitMonthAgenda,
  toPlanningTarget,
  weekDays,
  type AgendaEntry,
  type FlowStep,
  type LoadMonth,
  type NavTarget,
  type TodoAction,
} from './dashboardModel';

type OpenFn = (target: NavTarget | null) => void;

/* ───────────────────────── Small building blocks ───────────────────────── */

function Shimmer({ width = '60%' }: { width?: string }) {
  return <span className="dash-c-shimmer" style={{ width }} aria-hidden="true" />;
}

function HonestValue({ children }: { children: ReactNode }) {
  return <span className="dash-c-honest-value">{children}</span>;
}

function ActionButton({ label, target, onOpen, primary = false }: { label: string; target: NavTarget | null | undefined; onOpen: OpenFn; primary?: boolean }) {
  if (!target) return null;
  return (
    <button type="button" className={`button button--compact${primary ? ' dash-c-action--strong' : ''}`} onClick={() => onOpen(target)}>
      {withProviderLogo(label)}
    </button>
  );
}

function LinkButton({ children, target, onOpen }: { children: ReactNode; target: NavTarget; onOpen: OpenFn }) {
  return (
    <button type="button" className="dash-c-link" onClick={() => onOpen(target)}>
      {children}
    </button>
  );
}

function sectionOf(payload: MyWeekPayload | undefined, key: string) {
  return (payload?.sections || []).find((section) => section.key === key);
}

function stageOf(item: MyWeekItem): string {
  const stage = safeString(item.meta).split(' · ')[0].trim();
  return !stage || stage.toLowerCase().startsWith('statut non renseign') ? '' : stage;
}

const WEEK_SOURCE_KEY: Record<SourceConnector, string> = {
  hubspot: 'pipeline',
  qonto: 'facturation',
  digiforma: 'sessions_training',
};

/** Real state of a source of the week payload, with the action that fixes it. */
function weekSource(payload: MyWeekPayload | undefined, connector: SourceConnector): SourceFix {
  const diagnosis = diagnoseSource(connector, payload?.sources?.[WEEK_SOURCE_KEY[connector]]);
  // The payload says the source is not usable although its copy looks sound:
  // it has not delivered this week's data, which a synchronisation refreshes.
  if (!diagnosis.cause) {
    return {
      cause: 'source_not_synced',
      statusLabel: CAUSE_STATUS_LABEL.source_not_synced,
      reason: `${CONNECTOR_LABEL[connector]} n’a pas fourni les données de la semaine.`,
      action: syncAction(connector),
    };
  }
  return diagnosis;
}

/**
 * The week payload feeds several blocks; only the agenda carries the retry
 * button (one button per intent), the others point to it.
 */
function MyWeekFailure({ query, title, retry = false }: { query: UseQueryResult<MyWeekPayload>; title: string; retry?: boolean }) {
  return (
    <HonestState
      title={title}
      detail={retry ? (query.error instanceof Error ? query.error.message : 'La vue de la semaine n’a pas répondu.') : 'Réessayer depuis l’agenda'}
      action={retry ? <button type="button" className="button button--compact" onClick={() => query.refetch()}>Réessayer</button> : undefined}
    />
  );
}

/* ───────────────────────── Opportunités sans prochaine action ───────────────────────── */

/**
 * The deal's company: its logo before its name (Pascal, 30/09/2026 : « ici, il
 * manque les logos des sociétés »). « client non lié » stays plain text, and
 * so does the subtitle of an older server, which may be that sentence.
 */
function DealClient({ item }: { item: MyWeekItem }) {
  const client = myWeekItemClient(item);
  if (client === undefined) return <>{safeString(item.subtitle) || 'client non lié'}</>;
  return client ? <ClientName name={client.name} src={client.logo} /> : <>client non lié</>;
}

function DealRow({ item, onOpen, compact = false }: { item: MyWeekItem; onOpen: OpenFn; compact?: boolean }) {
  const target = fromMyWeekItem(item);
  const stage = stageOf(item);
  return (
    <div className={`dash-c-deal${compact ? ' dash-c-deal--compact' : ''}`}>
      <span className="dash-c-deal__identity">
        {target ? (
          <button type="button" className="dash-c-item-title" onClick={() => onOpen(target)}>{item.title}</button>
        ) : (
          <span className="dash-c-item-title">{item.title}</span>
        )}
        <span className="dash-c-deal__client"><DealClient item={item} /></span>
      </span>
      <span className="dash-c-deal__stage">
        <Chip tone={stage ? 'pipeline' : 'muted'}>{stage || 'Étape non renseignée'}</Chip>
      </span>
      <span className={`dash-c-deal__amount num${item.amount_label ? '' : ' is-missing'}`}>
        {item.amount_label || 'sans montant'}
      </span>
      {item.external_url ? (
        <a className="button button--compact dash-c-deal__action" href={item.external_url} target="_blank" rel="noopener noreferrer">
          <ProviderLogo provider="hubspot" />Dater dans HubSpot
        </a>
      ) : null}
    </div>
  );
}

function hubspotReady(payload: MyWeekPayload | undefined): boolean {
  return payload?.metrics?.hubspot_available === true;
}

/* ───────────────────────── Vue Semaine ───────────────────────── */

interface WeekViewProps {
  dashboard: DashboardPayload;
  myWeek: UseQueryResult<MyWeekPayload>;
  year: number;
  weekStart: string;
  today: Date;
  onOpen: OpenFn;
}

function weekMonthKey(payload: MyWeekPayload | undefined, weekStart: string): string {
  const fromPayload = safeString(payload?.week?.month_key);
  return /^\d{4}-\d{2}$/.test(fromPayload) ? fromPayload : weekStart.slice(0, 7);
}

function FreeDaysStat({ month, monthKey, label, onOpen, withOverload }: { month: LoadMonth | undefined; monthKey: string; label: string; onOpen: OpenFn; withOverload: boolean }) {
  const planning = toPlanningTarget({ monthKey });
  if (!month) {
    return (
      <StatCard
        label={label}
        value={<HonestValue>Charge non calculée</HonestValue>}
        detail={`Aucune ligne de planification pour ${monthName(monthKey)}`}
        tone="muted"
      >
        <span className="dash-c-stat-action">
          <ActionButton label={`Planifier ${monthName(monthKey)}`} target={planning} onOpen={onOpen} />
        </span>
      </StatCard>
    );
  }
  const over = month.overload > 0.01;
  const detail = `Claire chargée ${formatDays(month.load)} j sur ${formatDays(month.capacity)}`;
  return (
    <StatCard
      label={label}
      value={`${formatDays(over ? 0 : month.remaining)} j`}
      detail={over && withOverload ? `${detail} · ${formatDays(month.overload)} j à sous-traiter` : detail}
      tone={over ? 'alert' : 'default'}
      onClick={() => onOpen(planning)}
    />
  );
}

function WeekStats({ dashboard, myWeek, weekStart, today, onOpen }: WeekViewProps) {
  const payload = myWeek.data;
  const metrics = payload?.metrics || {};
  const monthKey = weekMonthKey(payload, weekStart);
  const loadMonth = buildLoadMonths(dashboard, today).find((month) => month.month === monthKey);
  const loading = myWeek.isLoading;
  const failed = myWeek.isError && !payload;

  const sessionsCard = (() => {
    if (loading) return <StatCard label="Sessions" value={<Shimmer width="30%" />} />;
    if (failed) return <StatCard label="Sessions" value={<HonestValue>Non chargées</HonestValue>} detail="Réessayer depuis l’agenda" tone="muted" />;
    const count = toNumber(metrics.sessions);
    const unassigned = toNumber(metrics.unassigned_sessions);
    const partial = metrics.sessions_partial === true;
    if (partial && count === 0) {
      // The agenda below carries the Digiforma action (one button per intent).
      const digiforma = weekSource(payload, 'digiforma');
      return <StatCard label="Sessions" value={<HonestValue>{sourceStateTitle('digiforma', digiforma)}</HonestValue>} detail="Même correction que « Agenda »" tone="muted" />;
    }
    const detail = partial
      ? 'hors formations Digiforma (non synchronisé)'
      : unassigned > 0
        ? `${unassigned} sans intervenant`
        : count > 0 ? 'toutes avec intervenant' : 'aucune session datée';
    return <StatCard label="Sessions" value={count} detail={detail} tone={unassigned > 0 ? 'alert' : 'default'} />;
  })();

  const receivablesCard = (() => {
    const label = 'Créances à relancer';
    if (loading) return <StatCard label={label} value={<Shimmer />} />;
    if (failed) return <StatCard label={label} value={<HonestValue>Non chargées</HonestValue>} detail="Réessayer depuis l’agenda" tone="muted" />;
    if (metrics.qonto_snapshot_available !== true) {
      const qonto = weekSource(payload, 'qonto');
      return (
        <StatCard label={label} value={<HonestValue>{sourceStateTitle('qonto', qonto)}</HonestValue>} detail="Aucune créance comptée, ni total ni zéro" tone="muted">
          <span className="dash-c-stat-action"><FixActionButton action={qonto.action} /></span>
        </StatCard>
      );
    }
    if (metrics.qonto_available !== true) {
      const coverage = optionalNumber(metrics.qonto_currency_eur_coverage_pct);
      return (
        <StatCard
          label={label}
          value={<HonestValue>Montants masqués</HonestValue>}
          detail={coverage === null ? 'Dates, montants ou devises manquants sur des factures Qonto' : `Devise EUR confirmée pour ${Math.round(coverage)} % des factures`}
          tone="muted"
        >
          <span className="dash-c-stat-action">
            <FixActionButton action={{ kind: 'navigate', label: 'Voir les factures incomplètes', target: incompleteInvoicesTarget() }} />
          </span>
        </StatCard>
      );
    }
    const amount = toNumber(metrics.invoice_amount);
    const count = toNumber(metrics.invoices);
    const items = sectionOf(payload, 'creances_qonto')?.items || [];
    // Each invoice is listed with its own « Ouvrir la facture » in « À traiter »:
    // the figure itself stays informative (one button per intent).
    const oldest = items.find((item) => item.date);
    const oldestDate = oldest?.date ? shortDate(oldest.date, today.getFullYear()) : '';
    const oldestDue = oldest?.date ? parseIsoDate(oldest.date) : null;
    const oldestPast = oldestDue ? oldestDue < today : false;
    const detail = count === 0
      ? 'aucune facture échue ni à échéance'
      : count === 1
        ? `1 facture${oldestDate ? ` · ${oldestPast ? 'échue depuis le' : 'échéance le'} ${oldestDate}` : ''}`
        : `${count} factures${oldestDate ? ` · la plus ancienne due le ${oldestDate}` : ''}`;
    return <StatCard label={label} value={formatCurrency(amount)} detail={detail} tone={amount > 0 ? 'alert' : 'default'} />;
  })();

  const hubspotCard = (() => {
    const label = 'Actions HubSpot datées';
    if (loading) return <StatCard label={label} value={<Shimmer width="30%" />} />;
    if (failed) return <StatCard label={label} value={<HonestValue>Non chargées</HonestValue>} detail="Réessayer depuis l’agenda" tone="muted" />;
    if (!hubspotReady(payload)) {
      // « Opportunités sans prochaine action » below carries the HubSpot action.
      const hubspot = weekSource(payload, 'hubspot');
      return <StatCard label={label} value={<HonestValue>{sourceStateTitle('hubspot', hubspot)}</HonestValue>} detail="Même correction que « Opportunités sans prochaine action »" tone="muted" />;
    }
    const count = toNumber(metrics.opportunities);
    return (
      <StatCard
        label={label}
        value={count}
        detail={count === 0 ? 'aucune relance prévue cette semaine' : `relance${count > 1 ? 's' : ''} dans l’agenda`}
      />
    );
  })();

  return (
    <section className="dash-c-stats dash-c-stats--week" aria-label="La semaine en chiffres">
      {sessionsCard}
      {receivablesCard}
      <FreeDaysStat month={loadMonth} monthKey={monthKey} label={`Jours libres en ${monthName(monthKey)}`} onOpen={onOpen} withOverload={false} />
      {hubspotCard}
    </section>
  );
}

interface WeekAgendaEntry {
  key: string;
  title: string;
  subtitle: string;
  kind: 'session' | 'pipeline';
  target: NavTarget | null;
  /** Session: the client's logo left of the title, its name on hover. */
  titleClient?: ItemClient | null;
  /** Relance: the deal's company, cited in the subtitle with its logo. */
  subtitleClient?: ItemClient | null;
}

function WeekAgenda({ myWeek, weekStart, today, onOpen }: WeekViewProps) {
  const payload = myWeek.data;
  const days = weekDays(weekStart, today);
  const byDay = useMemo(() => {
    const map = new Map<string, WeekAgendaEntry[]>();
    const push = (iso: string, entry: WeekAgendaEntry) => map.set(iso, [...(map.get(iso) || []), entry]);
    for (const item of sectionOf(payload, 'sessions')?.items || []) {
      const iso = safeString(item.date).slice(0, 10);
      if (!iso) continue;
      push(iso, { key: `session-${item.key}`, title: item.title, subtitle: safeString(item.subtitle), kind: 'session', target: fromMyWeekItem(item), titleClient: myWeekItemClient(item) });
    }
    for (const item of sectionOf(payload, 'pipeline')?.items || []) {
      const iso = safeString(item.date).slice(0, 10);
      if (!iso) continue;
      const client = myWeekItemClient(item);
      const company = client === undefined ? safeString(item.subtitle) || 'client non lié' : client?.name || 'client non lié';
      push(iso, { key: `deal-${item.key}`, title: item.title, subtitle: `Relance HubSpot · ${company}`, kind: 'pipeline', target: fromMyWeekItem(item), subtitleClient: client });
    }
    return map;
  }, [payload]);
  const sessionsShown = sectionOf(payload, 'sessions')?.items.length || 0;
  const hiddenSessions = Math.max(0, toNumber(payload?.metrics?.sessions) - sessionsShown);
  const monthKey = weekStart.slice(0, 7);
  const partial = payload?.metrics?.sessions_partial === true;

  return (
    <Panel
      title="Agenda"
      className="dash-c-agenda"
      aside={<LinkButton target={toCalendrierTarget({ monthKey })} onOpen={onOpen}>Ouvrir le calendrier</LinkButton>}
    >
      {myWeek.isError && !payload ? <MyWeekFailure query={myWeek} title="Agenda non chargé" retry /> : (
        <>
          <div className="dash-c-days" role="list" aria-label="Les 7 jours de la semaine">
            {days.map((day) => {
              const entries = byDay.get(day.iso) || [];
              return (
                <div
                  key={day.iso}
                  role="listitem"
                  className={`dash-c-day${day.isToday ? ' is-today' : ''}${day.weekend ? ' is-weekend' : ''}`}
                  aria-current={day.isToday ? 'date' : undefined}
                >
                  <span className="dash-c-day__dow">{day.dow}</span>
                  <span className="dash-c-day__num num">{day.day}</span>
                  {myWeek.isLoading ? <Shimmer width="80%" /> : null}
                  {entries.map((entry) => {
                    // One logo per mention (30/09/2026): left of a session's title,
                    // before the company of a relance; the tooltip stays text.
                    const body = (
                      <>
                        <strong>
                          {entry.titleClient ? <ClientTitleLogo name={entry.titleClient.name} src={entry.titleClient.logo} className="c-client-title-logo--xs" /> : null}
                          {entry.title}
                        </strong>
                        {entry.subtitleClient ? (
                          <span>
                            {withProviderLogo('Relance HubSpot · ')}
                            <ClientName name={entry.subtitleClient.name} src={entry.subtitleClient.logo} />
                          </span>
                        ) : entry.subtitle ? <span>{withProviderLogo(entry.subtitle)}</span> : null}
                      </>
                    );
                    return entry.target ? (
                      <button
                        key={entry.key}
                        type="button"
                        className={`dash-c-event dash-c-event--${entry.kind}`}
                        title={[entry.title, entry.subtitle].filter(Boolean).join(' · ')}
                        onClick={() => onOpen(entry.target)}
                      >
                        {body}
                      </button>
                    ) : (
                      <div key={entry.key} className={`dash-c-event dash-c-event--${entry.kind}`}>{body}</div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          {hiddenSessions > 0 ? (
            <p className="dash-c-note num">{hiddenSessions} autre{hiddenSessions > 1 ? 's' : ''} session{hiddenSessions > 1 ? 's' : ''} dans le calendrier</p>
          ) : null}
          {partial ? (() => {
            const digiforma = weekSource(payload, 'digiforma');
            return (
              <HonestState
                title="Formations Digiforma absentes"
                detail={`${digiforma.reason} Seules les sessions Fibery sont affichées.`}
                action={<FixActionButton action={digiforma.action} />}
              />
            );
          })() : null}
        </>
      )}
    </Panel>
  );
}

/** The opportunities come from HubSpot: its real state, and the action that fixes it. */
function HubspotMissing({ payload }: { payload: MyWeekPayload }) {
  const hubspot = weekSource(payload, 'hubspot');
  return (
    <HonestState
      title="Opportunités HubSpot non chargées"
      detail={hubspot.reason}
      action={<FixActionButton action={hubspot.action} />}
    />
  );
}

function UndatedDeals({ myWeek, onOpen }: { myWeek: UseQueryResult<MyWeekPayload>; onOpen: OpenFn }) {
  const payload = myWeek.data;
  const section = sectionOf(payload, 'pipeline_a_dater');
  const total = toNumber(payload?.metrics?.opportunities_to_date ?? section?.count);
  const ready = hubspotReady(payload);
  return (
    <Panel
      title="Opportunités sans prochaine action"
      className="dash-c-deals"
      aside={ready && total > 0 ? (
        <>
          <span className="num">{total}</span>
          <LinkButton target={toPipelineTarget({ flag: 'needs-next-action' })} onOpen={onOpen}>Ouvrir le pipeline</LinkButton>
        </>
      ) : undefined}
    >
      {myWeek.isLoading ? <Shimmer width="70%" /> : null}
      {myWeek.isError && !payload ? <MyWeekFailure query={myWeek} title="Opportunités non chargées" /> : null}
      {payload && !ready ? <HubspotMissing payload={payload} /> : null}
      {payload && ready && section && section.items.length === 0 ? (
        <p className="dash-c-note">Toutes les opportunités ouvertes ont une prochaine action datée.</p>
      ) : null}
      {payload && ready && section?.items.length ? (
        <div className="dash-c-deal-list">
          {section.items.map((item) => <DealRow key={item.key} item={item} onOpen={onOpen} />)}
        </div>
      ) : null}
    </Panel>
  );
}

const TODO_VISIBLE = 6;

function TodoList({ actions, onOpen, loading, failure }: { actions: TodoAction[]; onOpen: OpenFn; loading: boolean; failure: ReactNode }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? actions : actions.slice(0, TODO_VISIBLE);
  const hidden = actions.length - Math.min(actions.length, TODO_VISIBLE);
  return (
    <Panel title="À traiter" className="dash-c-todo" aside={<span className="num" aria-label={`${actions.length} action(s)`}>{actions.length}</span>}>
      {failure}
      {actions.length === 0 && !loading && !failure ? (
        <HonestState title="Rien à traiter" detail="Aucune créance, anomalie, correction ni surcharge détectée pour cette semaine." />
      ) : null}
      {actions.length ? (
        <ul className="dash-c-todo__list" id="dash-c-todo-list">
          {visible.map((action) => (
            <li key={action.key} className="dash-c-todo__item">
              <span className={`dash-c-dot dash-c-dot--${action.tone}`} aria-hidden="true" />
              <span className="dash-c-todo__text">
                <span className="dash-c-todo__title">{withProviderLogo(action.title)}</span>
                {action.detail ? <span className="dash-c-todo__detail num">{action.detail}</span> : null}
              </span>
              <span className="dash-c-todo__action">
                {action.sync
                  ? <SyncSourceButton connector={action.sync} label={action.actionLabel} primary />
                  : <ActionButton label={action.actionLabel} target={action.target} onOpen={onOpen} primary />}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {loading ? <Shimmer width="70%" /> : null}
      {hidden > 0 ? (
        <button
          type="button"
          className="dash-c-link dash-c-more"
          aria-expanded={expanded}
          aria-controls="dash-c-todo-list"
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? 'Réduire la liste' : `Afficher les ${hidden} autres`}
        </button>
      ) : null}
    </Panel>
  );
}

export function WeekView(props: WeekViewProps) {
  const { dashboard, myWeek, year, today, onOpen } = props;
  const actions = useMemo(
    () => buildTodoActions({ dashboard, myWeek: myWeek.data, year, today }),
    [dashboard, myWeek.data, today, year],
  );
  return (
    <>
      <WeekStats {...props} />
      <div className="dash-c-week">
        <div className="dash-c-week__main">
          <WeekAgenda {...props} />
          <UndatedDeals myWeek={myWeek} onOpen={onOpen} />
        </div>
        <TodoList
          actions={actions}
          onOpen={onOpen}
          loading={myWeek.isLoading}
          failure={myWeek.isError && !myWeek.data ? <MyWeekFailure query={myWeek} title="Priorités de la semaine non chargées" /> : null}
        />
      </div>
    </>
  );
}

/* ───────────────────────── Vue Mois ───────────────────────── */

interface MonthViewProps {
  dashboard: DashboardPayload;
  myWeek: UseQueryResult<MyWeekPayload>;
  year: number;
  monthKey: string;
  today: Date;
  onOpen: OpenFn;
}

const AGENDA_KIND_LABEL: Record<AgendaEntry['kind'], string> = {
  formation: 'Formation',
  session: 'Autre session',
  event: 'Évènement',
};

const NO_IDENTITY = 'sans fiche à ouvrir dans le calendrier';

function agendaLabel(entry: AgendaEntry): string {
  return [entry.title, formatDateRangeShort(entry), entry.resource].filter(Boolean).join(' · ');
}

/**
 * Entries covering more than two weeks, in one line above the grid instead of
 * a bar repeated on every week. Each entry opens THAT session in the calendar;
 * entries sharing their dates and title start read as one mention.
 */
function OngoingEntries({ entries, onOpen }: { entries: AgendaEntry[]; onOpen: OpenFn }) {
  const labelId = useId();
  const groups = useMemo(() => groupOngoingEntries(entries), [entries]);
  if (!groups.length) return null;
  const entryButton = (entry: AgendaEntry, content: ReactNode, className: string) => {
    const target = agendaEntryTarget(entry);
    const label = agendaLabel(entry);
    return target ? (
      <button type="button" className={className} title={label} aria-label={label} onClick={() => onOpen(target)}>{content}</button>
    ) : (
      <span className={`${className} is-static`} title={`${label} · ${NO_IDENTITY}`}>{content}</span>
    );
  };
  return (
    <div className="dash-c-ongoing" role="group" aria-labelledby={labelId}>
      <span id={labelId} className="dash-c-ongoing__label">En cours tout le mois</span>
      <ul className="dash-c-ongoing__list">
        {groups.map((group) => (
          <li key={group.key}>
            {group.entries.length === 1 ? entryButton(
              group.entries[0].entry,
              <>
                <GroupClientLogo entries={group.entries} />
                <strong>{group.label}</strong>
                <span className="num">{group.range}</span>
              </>,
              `dash-c-ongoing__item dash-c-bar--${group.kind}`,
            ) : (
              <span className={`dash-c-ongoing__item dash-c-ongoing__item--group dash-c-bar--${group.kind}`} role="group" aria-label={`${group.label} · ${group.range}`}>
                <GroupClientLogo entries={group.entries} />
                <strong>{group.label}</strong>
                <span className="num">{group.range}</span>
                {group.entries.map(({ entry, short }) => (
                  <Fragment key={entry.id || short}>{entryButton(entry, short, 'dash-c-ongoing__pick')}</Fragment>
                ))}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The client shared by every entry of an « En cours tout le mois » group:
 * its logo leads the group's title. Entries of different clients (or
 * without one) show none rather than a wrong one.
 */
function GroupClientLogo({ entries }: { entries: Array<{ entry: AgendaEntry }> }) {
  const first = entries[0]?.entry.client;
  if (!first || entries.some(({ entry }) => entry.client?.name !== first.name)) return null;
  return <ClientTitleLogo name={first.name} src={first.logo} className="c-client-title-logo--xs" />;
}

function MonthCalendar({ entries, monthKey, today, onOpen }: { entries: AgendaEntry[]; monthKey: string; today: Date; onOpen: OpenFn }) {
  const weeks = monthGrid(monthKey, today);
  const first = parseIsoDate(weeks[0][0].iso) as Date;
  const last = parseIsoDate(weeks[weeks.length - 1][6].iso) as Date;
  const rangeLabel = `${monthTitle(monthKey)}, du ${first.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} au ${last.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}`;
  const dows = weeks[0].map((day) => (parseIsoDate(day.iso) as Date).toLocaleDateString('fr-FR', { weekday: 'short' }));
  return (
    <div className="dash-c-cal" role="group" aria-label={rangeLabel}>
      <div className="dash-c-cal__head" aria-hidden="true">
        {dows.map((dow) => <span key={dow}>{dow}</span>)}
      </div>
      <div className="dash-c-cal__body">
        {weeks.map((week) => {
          const { segments, hidden } = layoutWeek(entries, week);
          return (
            <div key={week[0].iso} className="dash-c-cal__week">
              {week.map((day, index) => (
                <div
                  key={day.iso}
                  className={`dash-c-cal__day${day.inMonth ? '' : ' is-outside'}${day.weekend ? ' is-weekend' : ''}`}
                  style={{ gridColumn: index + 1 }}
                  aria-current={day.isToday ? 'date' : undefined}
                >
                  <span className={`dash-c-cal__num num${day.isToday ? ' is-today' : ''}`}>{day.day}</span>
                  {day.monthTag ? <span className="dash-c-cal__tag">{day.monthTag}</span> : null}
                </div>
              ))}
              {segments.map((segment) => {
                const entry = segment.entry;
                const label = agendaLabel(entry);
                const target = agendaEntryTarget(entry);
                const className = `dash-c-bar dash-c-bar--${entry.kind}${segment.continuesBefore ? ' is-cont-before' : ''}${segment.continuesAfter ? ' is-cont-after' : ''}${target ? '' : ' is-static'}`;
                const style = { gridColumn: `${segment.col} / span ${segment.span}`, gridRow: segment.lane + 2 };
                // The client's logo leads the bar (30/09/2026), 14 px inside the
                // 20 px bar: the week rows keep their height.
                const logo = entry.client ? <ClientTitleLogo name={entry.client.name} src={entry.client.logo} className="c-client-title-logo--xs" /> : null;
                return target ? (
                  <button key={`${entry.id}-${week[0].iso}`} type="button" className={className} style={style} title={label} aria-label={label} onClick={() => onOpen(target)}>
                    {logo}
                    {entry.title}
                  </button>
                ) : (
                  <span key={`${entry.title}-${segment.col}-${week[0].iso}`} className={className} style={style} title={`${label} · ${NO_IDENTITY}`}>
                    {logo}
                    {entry.title}
                  </span>
                );
              })}
              {hidden > 0 ? (
                <span className="dash-c-cal__more num" style={{ gridRow: 5, gridColumn: '1 / -1' }}>
                  +{hidden} autre{hidden > 1 ? 's' : ''} cette semaine
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LoadHistory({ months, monthKey }: { months: LoadMonth[]; monthKey: string }) {
  const shown = months.filter((month) => month.month <= monthKey && month.month.slice(0, 4) === monthKey.slice(0, 4));
  if (!shown.length) {
    return <HonestState title="Charge non calculée" detail={`Aucune ligne de planification jusqu’à ${monthName(monthKey)}`} />;
  }
  const max = Math.max(1, ...shown.map((month) => month.load));
  return (
    <div className="dash-c-history">
      <span className="dash-c-history__unit">En jours</span>
      <div className="dash-c-history__bars" style={{ gridTemplateColumns: `repeat(${shown.length}, minmax(0, 1fr))` }}>
        {shown.map((month) => {
          const current = month.month === monthKey;
          const label = `${month.short} : ${formatDays(month.load)} j`;
          return (
            <span key={month.month} className={`dash-c-history__col${current ? ' is-current' : ''}`} role="img" aria-label={label} title={label}>
              {/* The selected month's value already heads the screen: not repeated. */}
              <span className="dash-c-history__value num">{current ? '' : formatDays(month.load)}</span>
              <span className="dash-c-history__bar" style={{ height: `${Math.max(2, (month.load / max) * 64)}px` }} />
              <span className="dash-c-history__month">{month.short}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

export function MonthView({ dashboard, myWeek, year, monthKey, today, onOpen }: MonthViewProps) {
  const range = calendarRangeForMonth(monthKey);
  const calendarQuery = useQuery({
    queryKey: ['tab', 'calendrier', year, range.from, range.to],
    queryFn: () => fetchTabData('calendrier', year, range),
    ...SOURCE_PROJECTION_QUERY_OPTIONS,
  });
  const entries = useMemo(() => parseAgendaEntries(calendarQuery.data), [calendarQuery.data]);
  const agenda = useMemo(() => splitMonthAgenda(entries, monthKey), [entries, monthKey]);
  const monthStart = parseIsoDate(`${monthKey}-01`) as Date;
  const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
  const monthSessions = entries.filter((entry) => entry.kind !== 'event' && overlapsRange(entry, monthStart, monthEnd));
  const presentKinds = new Set(entries.filter((entry) => overlapsRange(entry, monthStart, monthEnd)).map((entry) => entry.kind));
  const kinds = (Object.keys(AGENDA_KIND_LABEL) as Array<AgendaEntry['kind']>).filter((kind) => presentKinds.has(kind));
  const loadMonths = buildLoadMonths(dashboard, today);
  const loadMonth = loadMonths.find((month) => month.month === monthKey);
  const payload = myWeek.data;
  const undated = sectionOf(payload, 'pipeline_a_dater');
  const undatedTotal = toNumber(payload?.metrics?.opportunities_to_date ?? undated?.count);
  const topDeals = (undated?.items || []).slice(0, 3);
  const digiformaMissing = payload?.metrics?.digiforma_available === false;

  const sessionsCard = (() => {
    const label = 'Sessions datées';
    if (calendarQuery.isLoading) return <StatCard label={label} value={<Shimmer width="30%" />} />;
    if (calendarQuery.isError) {
      return <StatCard label={label} value={<HonestValue>Calendrier non chargé</HonestValue>} detail="Réessayer depuis l’agenda" tone="muted" />;
    }
    if (monthSessions.length === 0 && digiformaMissing) {
      const digiforma = weekSource(payload, 'digiforma');
      return <StatCard label={label} value={<HonestValue>{sourceStateTitle('digiforma', digiforma)}</HonestValue>} detail="Même correction que « Agenda »" tone="muted" />;
    }
    return (
      <StatCard
        label={label}
        value={monthSessions.length}
        detail={monthSessions.length ? sessionTypeSummary(monthSessions) : `aucune session en ${monthName(monthKey)}`}
      />
    );
  })();

  return (
    <>
      <section className="dash-c-stats dash-c-stats--month" aria-label="Le mois en chiffres">
        <FreeDaysStat month={loadMonth} monthKey={monthKey} label="Jours libres" onOpen={onOpen} withOverload />
        {loadMonth ? (
          <StatCard
            label="Sous-traitance"
            value={`${formatDays(loadMonth.subcontracted)} j`}
            detail={loadMonth.subcontracted > 0 ? 'jours externes planifiés' : 'aucun jour externe planifié'}
          />
        ) : (
          <StatCard label="Sous-traitance" value={<HonestValue>Non calculée</HonestValue>} detail="Même cause que les jours libres" tone="muted" />
        )}
        {sessionsCard}
      </section>
      <div className="dash-c-month">
        <Panel
          title="Agenda"
          className="dash-c-month__agenda"
          aside={(
            <>
              {kinds.map((kind) => (
                <span key={kind} className="dash-c-legend">
                  <span className={`dash-c-legend__swatch dash-c-legend__swatch--${kind}`} aria-hidden="true" />
                  {AGENDA_KIND_LABEL[kind]}
                </span>
              ))}
              <LinkButton target={toCalendrierTarget({ monthKey })} onOpen={onOpen}>Ouvrir le calendrier</LinkButton>
            </>
          )}
        >
          {calendarQuery.isError ? (
            <HonestState
              title="Calendrier non chargé"
              detail={calendarQuery.error instanceof Error ? calendarQuery.error.message : 'Le calendrier n’a pas répondu.'}
              action={<button type="button" className="button button--compact" onClick={() => calendarQuery.refetch()}>Réessayer</button>}
            />
          ) : (
            <>
              {digiformaMissing ? (() => {
                const digiforma = weekSource(payload, 'digiforma');
                return (
                  <HonestState
                    title="Formations Digiforma absentes"
                    detail={`${digiforma.reason} Seules les sessions Fibery sont affichées.`}
                    action={<FixActionButton action={digiforma.action} />}
                  />
                );
              })() : null}
              <OngoingEntries entries={agenda.ongoing} onOpen={onOpen} />
              <MonthCalendar entries={agenda.short} monthKey={monthKey} today={today} onOpen={onOpen} />
            </>
          )}
        </Panel>
        <div className="dash-c-month__side">
          <Panel title="Charge de Claire par mois">
            <LoadHistory months={loadMonths} monthKey={monthKey} />
          </Panel>
          <Panel title="À planifier pour remplir le mois" className="dash-c-deals">
            {myWeek.isLoading ? <Shimmer width="70%" /> : null}
            {myWeek.isError && !payload ? <MyWeekFailure query={myWeek} title="Opportunités non chargées" retry /> : null}
            {payload && !hubspotReady(payload) ? <HubspotMissing payload={payload} /> : null}
            {payload && hubspotReady(payload) && topDeals.length === 0 ? (
              <p className="dash-c-note">Toutes les opportunités ouvertes ont une prochaine action datée.</p>
            ) : null}
            {payload && hubspotReady(payload) && topDeals.length ? (
              <>
                <div className="dash-c-deal-list">
                  {topDeals.map((item) => <DealRow key={item.key} item={item} onOpen={onOpen} compact />)}
                </div>
                <div className="dash-c-panel-foot">
                  <span className="num">{topDeals.length} affichée{topDeals.length > 1 ? 's' : ''} sur {undatedTotal} sans prochaine action</span>
                  <LinkButton target={toPipelineTarget({ flag: 'needs-next-action' })} onOpen={onOpen}>Ouvrir le pipeline</LinkButton>
                </div>
              </>
            ) : null}
          </Panel>
        </div>
      </div>
    </>
  );
}

/* ───────────────────────── Vue Année ───────────────────────── */

interface YearViewProps {
  dashboard: DashboardPayload;
  year: number;
  today: Date;
  onOpen: OpenFn;
}

function FlowCard({ step }: { step: FlowStep }) {
  return (
    <article className={`dash-c-flow__step dash-c-flow__step--${step.tone}`} aria-label={step.label}>
      <header className="dash-c-flow__head">
        <span className="dash-c-flow__label">
          <span className={`dash-c-swatch dash-c-swatch--${step.tone}`} aria-hidden="true" />
          {step.label}
        </span>
        <span className="dash-c-flow__source">{withProviderLogo(step.source)}</span>
      </header>
      {step.value !== null ? (
        <>
          <span className="dash-c-flow__value num">{formatCurrency(step.value)}</span>
          {step.detail || step.info ? (
            <span className="dash-c-flow__detail num">
              {step.detail}
              {step.info ? <InfoHint label={`À propos de « ${step.label} »`}>{step.info}</InfoHint> : null}
            </span>
          ) : null}
          {step.share !== undefined ? (
            <span className="dash-c-flow__share">
              <ProgressBar value={step.share} tone={step.tone} label={step.shareLabel} />
              <span className="num">{step.shareLabel}</span>
            </span>
          ) : null}
        </>
      ) : step.honest ? (
        <>
          <span className={`dash-c-flow__honest dash-c-flow__honest--${step.tone}`}>{step.honest.title}</span>
          <span className="dash-c-flow__detail num">{step.honest.detail}</span>
          {step.honest.action ? (
            <span className="dash-c-flow__share">
              <FixActionButton action={step.honest.action} primary />
            </span>
          ) : null}
        </>
      ) : null}
    </article>
  );
}

function niceStep(max: number): number {
  if (max <= 12) return 5;
  if (max <= 30) return 10;
  return 20;
}

function LoadChart({ months, dashboard, year, onOpen }: { months: LoadMonth[]; dashboard: DashboardPayload; year: number; onOpen: OpenFn }) {
  if (!months.length) {
    return (
      <HonestState
        title="Charge non calculée"
        detail={`Aucune ligne de planification pour ${year}`}
        action={<ActionButton label="Ouvrir la planification" target={toPlanningTarget({ monthKey: `${year}-01` })} onOpen={onOpen} />}
      />
    );
  }
  const peak = Math.max(1, ...months.map((month) => Math.max(month.capacity, month.load + month.subcontracted)));
  const step = niceStep(peak);
  const scale = Math.ceil((peak * 1.1) / step) * step;
  const ticks = Array.from({ length: Math.floor(scale / step) }, (_, index) => (index + 1) * step).filter((tick) => tick < scale);
  const pct = (value: number) => `${Math.min(100, (value / scale) * 100)}%`;
  const pastCount = months.filter((month) => month.phase === 'past').length;
  const upcoming = months.filter((month) => month.phase !== 'past');
  const freeAhead = upcoming.reduce((sum, month) => sum + month.remaining, 0);
  const soldDays = optionalNumber((dashboard as Record<string, unknown>).jours_vendus);
  const columns = `repeat(${months.length}, minmax(0, 1fr))`;
  return (
    <>
      <div className="dash-c-load__scroll">
      <div className="dash-c-load__phases" style={{ gridTemplateColumns: columns }} aria-hidden="true">
        {pastCount > 0 ? <span className="is-past" style={{ gridColumn: `span ${pastCount}` }}>Mois clos</span> : null}
        {upcoming.length > 0 ? <span className="is-ahead" style={{ gridColumn: `span ${upcoming.length}` }}>En cours et à venir</span> : null}
      </div>
      <div className="dash-c-load__plot">
        {ticks.map((tick) => (
          <span key={tick} className="dash-c-load__tick" style={{ bottom: pct(tick) }}>
            <span className="num">{tick} j</span>
          </span>
        ))}
        <div className="dash-c-load__cols" style={{ gridTemplateColumns: columns }}>
          {months.map((month) => {
            const label = `${monthName(month.month)} : charge ${formatDays(month.load)} j`
              + `${month.subcontracted > 0 ? `, sous-traité ${formatDays(month.subcontracted)} j` : ''}`
              + `, capacité ${formatDays(month.capacity)} j`
              + `${month.absence > 0 ? ` (congés et formations déduits : ${formatDays(month.absence)} j)` : ''}`
              + `${month.overload > 0.01 ? `, ${formatDays(month.overload)} j à sous-traiter` : ''}`;
            return (
              <div key={month.month} className={`dash-c-load__col is-${month.phase}`} role="img" aria-label={label} title={label}>
                <span className="dash-c-load__cap" style={{ bottom: pct(month.capacity) }} />
                <span className="dash-c-load__stack" style={{ height: pct(month.load + month.subcontracted) }}>
                  {month.subcontracted > 0 ? <span className="dash-c-load__sub" style={{ flexGrow: month.subcontracted }} /> : null}
                  {month.load > 0 ? <span className="dash-c-load__bar" style={{ flexGrow: month.load }} /> : null}
                </span>
                <span className={`dash-c-load__value num${month.overload > 0.01 ? ' is-over' : ''}`} style={{ bottom: `calc(${pct(month.load + month.subcontracted)} + 4px)` }}>
                  {formatDays(month.load)}
                  {month.subcontracted > 0 ? <span className="dash-c-load__plus"> +{formatDays(month.subcontracted)}</span> : null}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="dash-c-load__months" style={{ gridTemplateColumns: columns }}>
        {months.map((month) => (
          <span key={month.month} className={`dash-c-load__month is-${month.phase}`}>
            <span className="dash-c-load__pill">{month.short}</span>
            {month.phase !== 'past' ? <span className="dash-c-load__free num">{formatDays(month.remaining)} j libres</span> : null}
          </span>
        ))}
      </div>
      </div>
      <div className="dash-c-panel-foot dash-c-panel-foot--rule">
        <span className="num">
          {soldDays !== null ? <><strong>{formatDays(soldDays)}</strong> jours vendus</> : null}
          {soldDays !== null && upcoming.length ? ' · ' : null}
          {upcoming.length ? (
            <>
              <strong>{formatDays(freeAhead)}</strong> jours libres {upcoming.length > 1 ? `de ${monthName(upcoming[0].month)} à ${monthName(upcoming[upcoming.length - 1].month)}` : `en ${monthName(upcoming[0].month)}`}
            </>
          ) : null}
        </span>
        <LinkButton target={toPlanningTarget({ monthKey: upcoming[0]?.month || `${year}-01` })} onOpen={onOpen}>Ouvrir la planification</LinkButton>
      </div>
    </>
  );
}

const INVOICE_STATE_CHIPS: Record<PlannedInvoiceState, { label: string; tone: Tone }> = {
  to_plan: { label: 'Planification à finir', tone: 'muted' },
  ready: { label: 'Brouillon à créer', tone: 'billed' },
  draft: { label: 'Brouillon à envoyer', tone: 'signed' },
  stale: { label: 'Brouillon à mettre à jour', tone: 'alert' },
};

/** « Factures à envoyer » of the observed period; a row opens THIS mission (its invoice section). */
export function InvoicesToSendPanel({ invoices, periodLabel, onOpen }: { invoices: InvoiceToSend[]; periodLabel: string; onOpen: OpenFn }) {
  return (
    <Panel
      title="Factures à envoyer"
      className="dash-c-invoices"
      aside={<span className="num" aria-label={`${invoices.length} facture(s)`}>{invoices.length}</span>}
    >
      {invoices.length === 0 ? (
        <HonestState title={`Aucune facture prévue ${periodLabel}`} detail="La date de facture se règle dans chaque mission : « Facture de fin de mission »." />
      ) : (
        <ul className="dash-c-invoices__list" aria-label="Factures à envoyer">
          {invoices.map((invoice) => {
            const chip = INVOICE_STATE_CHIPS[invoice.state] || INVOICE_STATE_CHIPS.to_plan;
            return (
              <li key={invoice.missionId} className="dash-c-invoices__item">
                <span className={`dash-c-invoices__date num${invoice.overdue ? ' is-late' : ''}`}>
                  {invoice.date.split('-').reverse().join('/')}
                </span>
                <span className="dash-c-invoices__mission">
                  <LinkButton target={toMissionsTarget({ entityId: invoice.missionId, scope: 'all_signed' })} onOpen={onOpen}>
                    {invoice.missionName}
                  </LinkButton>
                  {invoice.clientName ? <span className="dash-c-invoices__client">{invoice.clientName}</span> : null}
                </span>
                {invoice.overdue ? <Chip tone="error">En retard</Chip> : null}
                <Chip tone={chip.tone}>{chip.label}</Chip>
                <span className="dash-c-invoices__amount num">{formatCurrency(invoice.amount)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

const CATEGORY_SWATCHES = ['#2C3440', '#566070', '#8B95A3', '#B9C0CA', '#D5D9E0'];

function ClientsPanel({ year, onOpen }: { year: number; onOpen: OpenFn }) {
  const query = useQuery({
    queryKey: ['tab', 'clients', year],
    queryFn: () => fetchTabData('clients', year),
  });
  const rows = Array.isArray(query.data?.entities) ? (query.data.entities as TabEntity[]) : [];
  const { clients, over10k } = buildTopClients(rows);
  const top = clients[0]?.amount || 0;
  const tick = top > 10_000 ? `${(10_000 / top) * 100}%` : null;
  return (
    <Panel
      title={`Clients ${year}`}
      className="dash-c-clients"
      aside={clients.length ? <span className="num">CA prévu · {over10k} au-delà de 10 k€</span> : undefined}
    >
      {query.isLoading ? <Shimmer width="70%" /> : null}
      {query.isError ? (
        <HonestState
          title="Clients non chargés"
          detail={query.error instanceof Error ? query.error.message : 'La liste des clients n’a pas répondu.'}
          action={<button type="button" className="button button--compact" onClick={() => query.refetch()}>Réessayer</button>}
        />
      ) : null}
      {query.data && !clients.length ? (
        <HonestState title={`Aucun CA prévu par client en ${year}`} detail="Le CA prévu vient des missions Fibery reliées à un client." />
      ) : null}
      {clients.map((client) => (
        // The name opens THIS client and its hit area covers the row; the
        // HubSpot chip, above it, opens THIS client on its HubSpot line
        // instead. Same reasons and labels as the Clients list (§8.8).
        <div key={client.id || client.name} className="dash-c-client">
          <span className="dash-c-client__identity">
            <span className="dash-c-client__name">
              <ClientLogo name={client.name} src={client.logoUrl} />
              <button
                type="button"
                className="dash-c-client__label dash-c-client__open"
                onClick={() => onOpen(client.id ? toClientsTarget({ entityId: client.id, search: client.name }) : null)}
              >
                {client.name}
              </button>
              {client.hubspot && client.id ? (
                <button
                  type="button"
                  className="dash-c-client__link-hubspot"
                  title={client.hubspot.title}
                  aria-label={client.hubspot.action}
                  onClick={() => onOpen({
                    ...toClientsTarget({ entityId: client.id }),
                    // Linked in the client file, as in the Pipeline (Pascal, 29/09/2026).
                    route: `/clients?client=${encodeURIComponent(client.id)}&relier=hubspot`,
                  })}
                >
                  <Chip tone={client.hubspot.tone}>{`HubSpot · ${client.hubspot.label}`}</Chip>
                </button>
              ) : null}
            </span>
            <span className="dash-c-client__sub num">
              {client.missions} mission{client.missions > 1 ? 's' : ''} · {client.sessions} session{client.sessions > 1 ? 's' : ''}
            </span>
          </span>
          <span className="dash-c-client__bar">
            <ProgressBar value={top ? client.amount / top : 0} tone="signed" />
            {tick ? <span className="dash-c-client__tick" style={{ left: tick }} aria-hidden="true" /> : null}
          </span>
          <span className="dash-c-client__amount num">{formatCurrency(client.amount)}</span>
        </div>
      ))}
    </Panel>
  );
}

/**
 * The « Non catégorisé » share opens exactly the missions without a category
 * (Missions filtered « Sans catégorie »), where each one is completed.
 */
function CategoriesPanel({ dashboard, year, onOpen }: { dashboard: DashboardPayload; year: number; onOpen: OpenFn }) {
  const categories = buildCategories((dashboard as Record<string, unknown>).categorie_ca);
  const uncategorized = categories.some((category) => category.uncategorized);
  let swatchIndex = 0;
  const swatches = categories.map((category) => (category.uncategorized ? 'hatch' : CATEGORY_SWATCHES[Math.min(swatchIndex++, CATEGORY_SWATCHES.length - 1)]));
  const swatchStyle = (value: string) => (value === 'hatch' ? undefined : { background: value });
  return (
    <Panel title="Répartition par catégorie" className="dash-c-categories">
      {categories.length === 0 ? (
        <HonestState title={`Aucun CA de mission à répartir en ${year}`} detail="La répartition se calcule sur le montant des missions de l’exercice." />
      ) : (
        <>
          <div className="dash-c-categories__bar" role="img" aria-label={categories.map((category) => `${category.label} ${Math.round(category.share * 1000) / 10} %`).join(', ')}>
            {categories.map((category, index) => (
              <span
                key={category.key}
                className={swatches[index] === 'hatch' ? 'dash-c-hatch' : undefined}
                style={{ width: `${category.share * 100}%`, ...swatchStyle(swatches[index]) }}
              />
            ))}
          </div>
          <div className="dash-c-categories__rows">
            {categories.map((category, index) => (
              <div key={category.key} className="dash-c-categories__row">
                <span className={`dash-c-categories__swatch${swatches[index] === 'hatch' ? ' dash-c-hatch' : ''}`} style={swatchStyle(swatches[index])} aria-hidden="true" />
                <span className="dash-c-categories__label">{category.label}</span>
                <span className="dash-c-categories__share num">{(Math.round(category.share * 1000) / 10).toLocaleString('fr-FR')} %</span>
                <span className="dash-c-categories__amount num">{formatCurrency(category.amount)}</span>
              </div>
            ))}
          </div>
          {uncategorized ? (
            <div className="dash-c-note dash-c-note--inline">
              Catégorie à renseigner mission par mission
              <InfoHint label="Où renseigner la catégorie">
                La catégorie se renseigne dans la fiche de chaque mission : « Modifier la mission ». La part « Non catégorisé » regroupe les missions de l’exercice sans catégorie.
              </InfoHint>
              {/* The share counts the whole exercise; « Production » only lists missions still
                  to produce (from this month), so the list opens on every signed mission (05/10/2026). */}
              <ActionButton label="Voir les missions sans catégorie" target={toMissionsTarget({ flag: 'sans-categorie', scope: 'all_signed' })} onOpen={onOpen} />
            </div>
          ) : null}
        </>
      )}
    </Panel>
  );
}

/** Lines of production without a subcontracting cost, as planning cells to open. */
function unknownCostPlaces(raw: unknown): Array<{ missionId: string; month: string; label: string; target: NavTarget }> {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      const record = (entry && typeof entry === 'object' ? entry : {}) as Record<string, unknown>;
      const missionId = safeString(record.mission_id).trim();
      const month = safeString(record.month).trim();
      if (!missionId || !/^\d{4}-\d{2}$/.test(month)) return null;
      const params = new URLSearchParams({ mission: missionId, mois: month });
      return {
        missionId,
        month,
        label: `${safeString(record.mission).trim() || 'Mission'} · ${monthName(month)}`,
        target: { route: `/affectation?${params.toString()}`, view: 'affectation', filters: {} } as NavTarget,
      };
    })
    .filter((place): place is { missionId: string; month: string; label: string; target: NavTarget } => place !== null);
}

function MarginPanel({ dashboard, year, onOpen }: { dashboard: DashboardPayload; year: number; onOpen: OpenFn }) {
  const metrics = financialMetricsOf(dashboard);
  const coverage = (metrics.coverage || {}) as Record<string, unknown>;
  const margin = optionalNumber(metrics.contribution_margin);
  const marginPct = optionalNumber(metrics.contribution_margin_pct);
  const directCost = optionalNumber(metrics.direct_subcontracting_cost);
  const economic = optionalNumber(metrics.economic_margin);
  const economicStatus = safeString(metrics.economic_margin_status) || (economic === null ? 'internal_day_cost_not_configured' : 'available');
  const status = safeString(metrics.contribution_margin_status);
  let body: ReactNode;
  let tone: Tone = 'default';
  if (realisedMissing(dashboard, year)) {
    tone = 'alert';
    body = <HonestState title="Marge non calculable" detail="Le réalisé n’est pas saisi : voir l’étape « Produit »." />;
  } else if (margin !== null) {
    body = (
      <div className="dash-c-margin">
        <span className="dash-c-margin__value num">{formatCurrency(margin)}</span>
        <span className="dash-c-margin__detail num">
          {marginPct !== null ? `${marginPct.toLocaleString('fr-FR')} % du CA produit` : 'Sur le CA produit'}
          {directCost !== null ? ` · sous-traitance directe ${formatCurrency(directCost)}` : ''}
        </span>
        {economic !== null ? (
          <span className="dash-c-margin__detail num">Marge économique {formatCurrency(economic)} (coût interne inclus)</span>
        ) : economicStatus === 'internal_day_cost_not_configured' ? (
          <span className="dash-c-margin__detail dash-c-margin__fix">
            <span>
              Marge économique : coût journalier interne non configuré
              <InfoHint label="À propos de la marge économique">
                La marge économique retranche le coût journalier interne de Claire (jours produits × coût du jour). Tant qu’il n’est pas réglé, seule la marge contributive est affichée.
              </InfoHint>
            </span>
            <FixActionButton action={{ kind: 'navigate', label: 'Régler le coût interne', target: INTERNAL_DAY_COST_TARGET }} />
          </span>
        ) : null}
      </div>
    );
  } else if (status === 'subcontracting_cost_incomplete') {
    tone = 'alert';
    const unknownRows = toNumber(coverage.subcontracting_cost_unknown_row_count);
    const pct = optionalNumber(coverage.subcontracting_cost_coverage_pct);
    // Each line without a cost is a mission × month of the planning: the
    // buttons open THAT mission on THAT month (never the Intervenants list).
    const places = unknownCostPlaces(coverage.subcontracting_cost_unknown_rows);
    body = (
      <HonestState
        action={places.length ? (
          <span className="dash-c-margin__places">
            {places.slice(0, 3).map((place) => (
              <ActionButton key={`${place.missionId}-${place.month}`} label={place.label} target={place.target} onOpen={onOpen} />
            ))}
            {places.length > 3 ? <span className="dash-c-note num">{`et ${places.length - 3} autre${places.length - 3 > 1 ? 's' : ''} ligne${places.length - 3 > 1 ? 's' : ''}`}</span> : null}
          </span>
        ) : undefined}
        title="Coût de sous-traitance incomplet"
        detail={(
          <span className="dash-c-margin__detail">
            {[
              unknownRows > 0 ? `${unknownRows} ligne${unknownRows > 1 ? 's' : ''} de production sans coût` : '',
              pct !== null ? `couverture ${pct.toLocaleString('fr-FR')} %` : '',
            ].filter(Boolean).join(' · ') || 'Coûts de sous-traitance à compléter'}
            <InfoHint label="À propos du coût de sous-traitance">
              Chaque ligne de production réalisée doit porter son coût de sous-traitance : le CA sous-traité de la ligne, ou le coût de l’affectation de l’intervenant externe (jours × CJM de l’intervenant). Tant qu’une ligne n’en a pas, la marge n’est pas calculée plutôt que comptée à zéro.
            </InfoHint>
          </span>
        )}
      />
    );
  } else {
    body = (
      <HonestState
        title="Production non chargée"
        detail="La marge se calcule sur le réalisé Fibery."
        action={<ActionButton label="Ouvrir la planification" target={toPlanningTarget({ monthKey: `${year}-01` })} onOpen={onOpen} primary />}
      />
    );
  }
  return (
    <Panel title="Marge contributive" className={`dash-c-margin-panel${tone === 'alert' ? ' is-alert' : ''}`}>
      {body}
    </Panel>
  );
}

export function YearView({ dashboard, year, today, onOpen }: YearViewProps) {
  const steps = buildMoneyFlow(dashboard, year);
  const months = buildLoadMonths(dashboard, today).filter((month) => month.month.startsWith(String(year)));
  return (
    <>
      <section className="dash-c-flow" aria-label="Parcours de l’argent">
        {steps.map((step) => <FlowCard key={step.key} step={step} />)}
      </section>
      <Panel
        title="Charge et capacité de Claire"
        className="dash-c-load"
        aside={(
          <>
            <span className="dash-c-load__unit">en jours</span>
            <span className="dash-c-legend"><span className="dash-c-legend__swatch dash-c-legend__swatch--load" aria-hidden="true" />Charge</span>
            <span className="dash-c-legend"><span className="dash-c-legend__swatch dash-c-hatch dash-c-hatch--produced" aria-hidden="true" />Sous-traité</span>
            <span className="dash-c-legend"><span className="dash-c-legend__line" aria-hidden="true" />Capacité</span>
          </>
        )}
      >
        <LoadChart months={months} dashboard={dashboard} year={year} onOpen={onOpen} />
      </Panel>
      <div className="dash-c-year-bottom">
        <ClientsPanel year={year} onOpen={onOpen} />
        <CategoriesPanel dashboard={dashboard} year={year} onOpen={onOpen} />
        <MarginPanel dashboard={dashboard} year={year} onOpen={onOpen} />
      </div>
    </>
  );
}

